/*
 * The attachment library (2026-10-28, S47).
 *
 * Every downloadable file the knowledge base holds, whichever articles list or
 * link it - and the place for the two changes that reach every one of those
 * articles at once: REPLACE (new bytes, and the name they came with, under the
 * same id, so every list entry and every [[attachment:...]] link now downloads
 * the new file) and DELETE (the file goes, off every list; a link to it in a
 * text reads as plain words). The article editor only lists and unlists.
 *
 * A delete first asks the server who uses the file - listed, or linked in a
 * current or latest text - and shows that before anything is removed; the
 * server refuses the delete while the file is used unless that was shown.
 *
 * Files go up through the website and down from the storage account, through a
 * link the server signs for one file for a few minutes; none of that is visible
 * here except that both need the deployment's attachment store, and say so when
 * it is missing.
 *
 * Its words come from the chosen language's dictionary, `t.admin.site` (S48).
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { AdminAttachment, AdminAttachmentDetail } from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { useAction, useAsync } from '../hooks/useAsync.js';
import { useAttachmentDownload } from '../hooks/useAttachmentDownload.js';
import { useAttachmentsEnabled } from '../hooks/useAttachmentsEnabled.js';
import { DateTime, ErrorBanner, Loading, Modal } from '../components/ui.js';
import { useMessages } from '../i18n/language.js';
import { richText } from '../i18n/richText.js';
import { AttachmentStoreOffNotice, byFileName } from './components/ArticleAttachmentsPanel.js';
import {
  AttachmentFilePicker,
  AttachmentUploadList,
  useAttachmentUploads,
  type AttachmentFilePickerHandle,
} from './components/AttachmentUploads.js';
import { attachmentRowId } from './components/attachmentFiles.js';

export function AttachmentsPage(): JSX.Element {
  const t = useMessages();
  const s = t.admin.site.attachments;
  const api = useAdminApi();
  const enabled = useAttachmentsEnabled();
  const library = useAsync(() => api.listAttachments(), []);
  const downloads = useAttachmentDownload();
  const location = useLocation();

  const uploadPicker = useRef<AttachmentFilePickerHandle>(null);
  const replacePicker = useRef<AttachmentFilePickerHandle>(null);
  const replaceTarget = useRef<AdminAttachment | null>(null);
  const [searchDraft, setSearchDraft] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminAttachment | null>(null);

  const items = library.data;
  const current = useRef(items);
  current.current = items;

  const uploads = useAttachmentUploads((response) => {
    const others = (current.current ?? []).filter((entry) => entry.attachmentId !== response.attachment.attachmentId);
    library.setData([...others, response.attachment].sort(byFileName));
    setNotice(
      response.replaced
        ? s.replacedNotice(response.attachment.fileName)
        : s.addedNotice(response.attachment.fileName),
    );
  });

  /*
   * Arriving at a file's row - from a link in the editor preview, opened in a
   * new tab - scrolls to it once the list is in, and the row is marked.
   */
  const targetId = location.hash.startsWith('#attachment-') ? location.hash.slice(1).toLowerCase() : null;
  useEffect(() => {
    if (!targetId || !items) return;
    document.getElementById(targetId)?.scrollIntoView({ block: 'center' });
  }, [targetId, items]);

  const search = searchDraft.trim().toLowerCase();
  const shown = (items ?? []).filter((attachment) => attachment.fileName.toLowerCase().includes(search));

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{s.title}</h1>
          <p className="faint">{s.intro}</p>
        </div>
        <div className="button-row">
          <button
            type="button"
            className="button button-primary"
            onClick={() => uploadPicker.current?.open()}
            disabled={!enabled}
            title={enabled ? s.uploadHint : s.noStore}
          >
            {s.upload}
          </button>
          <AttachmentFilePicker
            ref={uploadPicker}
            multiple
            label={s.uploadPicker}
            onFiles={(files) =>
              void uploads.start(files, (file, fileName, onProgress) =>
                api.uploadAttachment(file, { fileName, onProgress }),
              )
            }
          />
          <AttachmentFilePicker
            ref={replacePicker}
            label={s.replacePicker}
            onFiles={(files) => {
              const target = replaceTarget.current;
              if (!target || !files[0]) return;
              void uploads.start([files[0]], (file, fileName, onProgress) =>
                api.replaceAttachmentFile(target.attachmentId, file, { fileName, onProgress }),
              );
            }}
          />
        </div>
      </div>

      {!enabled && <AttachmentStoreOffNotice />}
      {downloads.error && <ErrorBanner error={downloads.error} onDismiss={downloads.clearError} />}
      {notice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">{notice}</div>
          <div className="notice-actions">
            <button type="button" className="button button-quiet" onClick={() => setNotice(null)}>
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}
      <AttachmentUploadList uploads={uploads.uploads} dismiss={uploads.dismiss} />

      <div className="card">
        <div className="field">
          <label htmlFor="attachment-search">{s.find}</label>
          <input
            id="attachment-search"
            type="search"
            placeholder={s.findPlaceholder}
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </div>

        {library.error && <ErrorBanner error={library.error} onRetry={library.reload} />}
        {library.loading && !items && <Loading label={s.loading} />}
        {items && items.length === 0 && <p className="faint">{s.empty}</p>}
        {items && items.length > 0 && shown.length === 0 && <p className="faint">{s.noMatch}</p>}

        {shown.length > 0 && (
          <ul className="attachment-list">
            {shown.map((attachment) => {
              const rowId = attachmentRowId(attachment.attachmentId);
              const replaced = attachment.modifiedAtUtc !== attachment.createdAtUtc;
              return (
                <li
                  key={attachment.attachmentId}
                  id={rowId}
                  className={`attachment-list-row${rowId === targetId ? ' attachment-list-row-target' : ''}`}
                >
                  <div className="attachment-list-detail">
                    <span className="attachment-name">{attachment.fileName}</span>
                    <span className="faint">
                      {t.article.fileType(attachment.fileName, attachment.contentType)} ·{' '}
                      {t.article.byteSize(attachment.byteLength)} ·{' '}
                      {richText(s.uploaded, {
                        date: <DateTime value={attachment.createdAtUtc} />,
                        name: attachment.createdBy,
                      })}
                      {replaced && (
                        <>
                          {' · '}
                          {richText(s.replaced, {
                            date: <DateTime value={attachment.modifiedAtUtc} />,
                            name: attachment.modifiedBy,
                          })}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="attachment-list-usage">
                    {attachment.listedBy === 0 ? (
                      <span className="badge badge-unwritten" title={s.notListedHint}>
                        {s.notListed}
                      </span>
                    ) : (
                      <span className="faint">{s.listedBy(attachment.listedBy)}</span>
                    )}
                  </div>
                  <div className="button-row">
                    <button
                      type="button"
                      className="button button-small button-quiet"
                      disabled={!enabled || downloads.running}
                      onClick={() => void downloads.download(() => api.attachmentDownloadLink(attachment.attachmentId))}
                      aria-label={t.article.downloadFile(attachment.fileName)}
                    >
                      {t.article.download}
                    </button>
                    <button
                      type="button"
                      className="button button-small"
                      disabled={!enabled}
                      onClick={() => {
                        replaceTarget.current = attachment;
                        replacePicker.current?.open();
                      }}
                      title={s.replaceHint}
                      aria-label={s.replaceFile(attachment.fileName)}
                    >
                      {s.replace}
                    </button>
                    <button
                      type="button"
                      className="button button-small button-quiet"
                      onClick={() => setPendingDelete(attachment)}
                      aria-label={s.deleteFile(attachment.fileName)}
                    >
                      {s.delete}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <DeleteAttachmentDialog
        attachment={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onDeleted={(deleted) => {
          library.setData((current.current ?? []).filter((entry) => entry.attachmentId !== deleted.attachmentId));
          setNotice(s.deletedNotice(deleted.fileName));
          setPendingDelete(null);
        }}
      />
    </>
  );
}

/**
 * Shows who uses the file before it goes. The usage is asked for when the
 * dialog opens, not taken from the list's count, because a text link is not in
 * that count and is exactly what a delete turns into plain words.
 */
function DeleteAttachmentDialog({
  attachment,
  onClose,
  onDeleted,
}: {
  attachment: AdminAttachment | null;
  onClose: () => void;
  onDeleted: (attachment: AdminAttachment) => void;
}): JSX.Element | null {
  const s = useMessages().admin.site.deleteAttachment;
  const api = useAdminApi();
  const action = useAction();
  const detail = useAsync<AdminAttachmentDetail | null>(
    () => (attachment ? api.getAttachment(attachment.attachmentId) : Promise.resolve(null)),
    [attachment?.attachmentId],
  );

  if (!attachment) return null;

  const usedBy = detail.data?.usedBy ?? null;

  const confirm = async (): Promise<void> => {
    const done = await action.run(async () => {
      // The flag says what this dialog showed. Sent only when it showed a use,
      // so a use that appeared since is refused rather than swept away.
      await api.deleteAttachment(attachment.attachmentId, { acknowledgeUsage: (usedBy?.length ?? 0) > 0 });
      return true;
    });
    if (done) onDeleted(attachment);
    else detail.reload();
  };

  return (
    <Modal
      open
      title={s.title(attachment.fileName)}
      onClose={onClose}
      busy={action.running}
      footer={
        <>
          <button type="button" className="button" onClick={onClose} disabled={action.running}>
            {s.cancel}
          </button>
          <button
            type="button"
            className="button button-danger"
            onClick={() => void confirm()}
            disabled={action.running || usedBy === null}
          >
            {s.confirm}
          </button>
        </>
      }
    >
      {action.error && <ErrorBanner error={action.error} onDismiss={action.clearError} />}
      {detail.error && <ErrorBanner error={detail.error} onRetry={detail.reload} />}
      {usedBy === null && !detail.error && <Loading label={s.finding} />}
      {usedBy !== null && usedBy.length === 0 && <p>{s.unused}</p>}
      {usedBy !== null && usedBy.length > 0 && (
        <>
          <p>{s.usedBy(usedBy.length)}</p>
          <ul>
            {usedBy.map((use) => (
              <li key={use.articleId}>
                <Link to={`/admin/articles/${use.articleId}`}>{use.title ?? use.slug}</Link>{' '}
                <span className="faint">
                  ({[use.listed ? s.listed : null, use.linked ? s.linked : null].filter(Boolean).join(', ')})
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="faint" style={{ marginBottom: 0 }}>
        {s.permanent}
      </p>
    </Modal>
  );
}
